import { useAuth } from "@clerk/clerk-react";
import {
  Box,
  Button,
  Grid,
  IconButton,
  Paper,
  Skeleton,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { BentoCategoriesContext } from "contexts/BentoCategories";
import { CampaignsContext } from "contexts/Campaigns";
import {
  AssistantOpenContext,
  DiscoverViewContext,
} from "contexts/DiscoverView";
import { OrganizationUserContext } from "contexts/Organization";
import { OutreachTemplatesContext } from "contexts/OutreachTemplates";
import { SubscriptionContext } from "contexts/Subscription";
import {
  Fragment,
  ReactNode,
  isValidElement,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useInView } from "react-intersection-observer";
import ReactMarkdown from "react-markdown";
import { useLocation, useNavigate } from "react-router-dom";
import remarkGfm from "remark-gfm";
import { BentoCategory, UpgradeDialogSource } from "schemas/dashboard";
import { paragraphFontFamily } from "styles/theme";

import { AI_DRAFT_CREDITS_TOOLTIP } from "constants/influencer";
import { MERGE_TAGS } from "constants/templates";
import { fetcherAuth } from "utils/api";
import { getInternalPath } from "utils/navigation";
import { sanitize } from "utils/sanitize";
import { trackEvent } from "utils/tracking";
import { useSubscription } from "utils/useSubscription";

import InteractiveChoicePicker, {
  InteractiveChoiceOption,
} from "./InteractiveChoicePicker";
import SuggestedPrompts from "./SuggestedPrompts";
import {
  ASSISTANT_AUTO_INTRO_SOURCE,
  AssistantEntityContext,
  ChipMetadata,
  PromptPage,
  SuggestedPrompt,
  useSuggestedPrompts,
} from "./api";
import styles from "./styles";

const MAX_PAGE_PROMPTS = 7;
const MAX_FOLLOWUP_PROMPTS = 5;
const MESSAGES_PER_PAGE = 50;
const INTRO_TYPING_MS = 650;
const INTRO_WORD_MS = 45;
const AUTO_INTRO_TEXT =
  "Hey! 👋 I'm your Bento assistant. I've been looking at your outreach and " +
  "I've got a few ideas to help you land more partnerships. Here are a few " +
  "things you can ask me 👇";

type IntroPhase = "idle" | "typing" | "streaming" | "shown";

const TOOL_STATUS_LABELS: Record<string, string> = {
  get_activity_overview: "Reviewing your activity",
  get_template_details: "Reviewing your templates",
  list_templates: "Looking up your templates",
  get_campaign_stats: "Reviewing your campaigns",
  get_niche_breakdown: "Analyzing your niches",
  get_action_item_counts: "Checking your inbox",
  list_pending_action_items: "Checking pending replies",
  get_check_in_count: "Checking your check-ins",
  list_pending_check_ins: "Checking your check-ins",
  draft_reply_for_action_item: "Drafting a reply",
  get_template_link: "Finding the template",
  get_action_item_link: "Finding the thread",
  list_niches: "Reviewing your niches",
  get_niche_link: "Finding the niche",
  list_bento_categories: "Loading brand niches",
  create_campaign_draft_from_chat: "Creating your campaign drafts",
};

function toolStatusLabel(name: string): string {
  return TOOL_STATUS_LABELS[name] ?? "Working on it";
}

// The model sometimes outputs `\/` instead of `/` inside markdown link URLs
// (a JSON-escape leak). Normalize before rendering so links resolve.
function normalizeAssistantText(text: string): string {
  return text.replace(/\\\//g, "/");
}

// Mirrors TipTap's paste pipeline so the template editor accepts our prefill body.
function plainTextToTemplateHtml(text: string): string {
  const escape = (s: string) =>
    s.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
  const mergeTagRe = new RegExp(`\\{(${MERGE_TAGS.join("|")})\\}`, "g");
  return text
    .replace(/\r\n?/g, "\n")
    .split("\n")
    .map((line) => {
      if (!line) return "<p></p>";
      const inner = escape(line).replace(
        mergeTagRe,
        (_match, label) =>
          `<span class="merge-tag" data-type="merge-tag" data-id="${label}" data-label="${label}">{${label}}</span>`,
      );
      return `<p>${inner}</p>`;
    })
    .join("");
}

// Pair every `?openCreate=` link in the message with the first code block, for editor prefill.
function buildTemplatePrefillMap(text: string): Map<string, string> {
  const map = new Map<string, string>();
  const codeBlockRe = /```([^\n]*)\n?([\s\S]*?)```/g;
  // Tolerate leading whitespace inside `( ... )` — models sometimes emit `[label]( /dashboard/...)`.
  const linkRe =
    /\[[^\]]*\]\(\s*(\/dashboard\/templates\?openCreate=[^)\s]*)\s*\)/g;
  const firstCodeMatch = codeBlockRe.exec(text);
  if (!firstCodeMatch) return map;
  const lang = (firstCodeMatch[1] || "").trim().toLowerCase();
  const body = firstCodeMatch[2].trim();
  if (!body) return map;
  const looksLikeHtml = lang === "html" || /^<[a-z]/i.test(body);
  const html = looksLikeHtml ? sanitize(body) : plainTextToTemplateHtml(body);
  let linkMatch: RegExpExecArray | null;
  while ((linkMatch = linkRe.exec(text)) !== null) {
    map.set(linkMatch[1], html);
  }
  return map;
}

type MessageSource =
  | "Typed"
  | "Suggested Prompt"
  | "Choice Picker"
  | "Floating Bubble";

type InteractivePrompt = {
  promptId: string;
  question: string;
  options: InteractiveChoiceOption[];
  multiSelect: boolean;
  minSelections: number;
  maxSelections: number | null;
  answered: boolean;
  selected: InteractiveChoiceOption[];
};

type ChatMessage = {
  id: string;
  role: "user" | "assistant" | "system" | "tool";
  text: string;
  createdAt: number;
  interactivePrompt?: InteractivePrompt;
};

type ServerMessage = {
  id: number;
  role: string;
  text: string | null;
  item: Record<string, unknown>;
  created_at: string | null;
};

// The campaign wizard's niche slot. The model sends this prompt with empty
// options; the FE fills them from BentoCategoriesContext so the model never
// has to transcribe the (large) category tree into a tool call.
const CATEGORIES_PROMPT_ID = "categories";

// Flatten the parent/subcategory tree into grouped picker options: one "select
// all" option per parent (label === group) followed by its subcategories.
function buildNicheOptions(
  topLevelCategories: BentoCategory[],
): InteractiveChoiceOption[] {
  const out: InteractiveChoiceOption[] = [];
  for (const parent of topLevelCategories) {
    out.push({ value: parent.name, label: parent.name, group: parent.name });
    for (const sub of parent.subcategories ?? []) {
      out.push({ value: sub.name, label: sub.name, group: parent.name });
    }
  }
  return out;
}

function derivePromptPage(pathname: string): PromptPage {
  if (pathname.startsWith("/dashboard/campaigns")) return "campaigns";
  if (pathname.startsWith("/dashboard/templates")) return "templates";
  if (pathname.startsWith("/dashboard/inbox")) return "inbox";
  if (pathname.startsWith("/dashboard/brands")) return "discover";
  return "home";
}

function nodeToText(node: ReactNode): string {
  if (node == null || typeof node === "boolean") return "";
  if (typeof node === "string") return node;
  if (typeof node === "number") return String(node);
  if (Array.isArray(node)) return node.map(nodeToText).join("");
  if (isValidElement(node)) {
    return nodeToText((node.props as { children?: ReactNode }).children);
  }
  return "";
}

function extractCodeLanguage(children: ReactNode): string | null {
  if (!isValidElement(children)) return null;
  const props = children.props as { className?: string };
  const match = (props.className || "").match(/language-(\w+)/);
  return match?.[1] ?? null;
}

// Plain-text clipboard flavor: paragraphs preserved as blank lines, merge tags as `{First Name}`.
function htmlToPlainText(html: string): string {
  if (typeof window === "undefined" || typeof DOMParser === "undefined") {
    return html;
  }
  const doc = new DOMParser().parseFromString(html, "text/html");
  doc.querySelectorAll("p").forEach((p) => {
    p.append(doc.createTextNode("\n"));
  });
  doc.querySelectorAll("br").forEach((br) => {
    br.replaceWith(doc.createTextNode("\n"));
  });
  const text = doc.body.textContent || "";
  return text.replace(/\n{3,}/g, "\n\n").trim();
}

const CodeBlock = ({
  children,
  language,
}: {
  children: ReactNode;
  language: string | null;
}) => {
  const [copied, setCopied] = useState(false);
  const rawText = nodeToText(children);
  // Treat the block as a template HTML preview when the fence said `html`
  // OR the body starts with a tag (covers the case where the model forgot
  // the language label but still emitted HTML).
  const isHtml = language === "html" || /^\s*<[a-z]/i.test(rawText);
  const plainText = isHtml ? htmlToPlainText(rawText) : rawText;
  const safeHtml = useMemo(
    () => (isHtml ? sanitize(rawText) : ""),
    [isHtml, rawText],
  );

  const handleCopy = async () => {
    try {
      // For HTML blocks, write both flavors so pasting into Gmail / the
      // template editor lands as formatted HTML, and pasting into plain-text
      // targets falls back cleanly. `navigator.clipboard.write` + ClipboardItem
      // is supported in all modern browsers; we fall back to writeText when
      // it isn't (e.g. older Safari, non-secure contexts).
      if (
        isHtml &&
        typeof ClipboardItem !== "undefined" &&
        navigator.clipboard.write
      ) {
        await navigator.clipboard.write([
          new ClipboardItem({
            "text/html": new Blob([rawText], { type: "text/html" }),
            "text/plain": new Blob([plainText], { type: "text/plain" }),
          }),
        ]);
      } else {
        await navigator.clipboard.writeText(plainText);
      }
      setCopied(true);
      window.setTimeout(() => setCopied(false), 2000);
    } catch {
      // ignore — clipboard write can fail in non-secure contexts
    }
  };

  return (
    <Box sx={{ position: "relative", my: 1 }}>
      <Button
        size="small"
        onClick={handleCopy}
        sx={{
          position: "absolute",
          top: 4,
          right: 4,
          minWidth: 0,
          px: 1,
          py: 0.25,
          fontSize: 11,
          textTransform: "none",
          color: copied ? "#008236" : "primary.main",
          backgroundColor: "rgba(255,255,255,0.9)",
          border: "1px solid rgba(1,67,57,0.1)",
          "&:hover": {
            backgroundColor: "rgba(255,255,255,1)",
          },
        }}
      >
        <Box
          component="i"
          className={copied ? "fa-regular fa-check" : "fa-regular fa-copy"}
          sx={{ fontSize: 11, mr: 0.5 }}
        />
        {copied ? "Copied" : "Copy"}
      </Button>
      {isHtml ? (
        // Styled preview matching the TipTap editor's render of the same body.
        <Box
          sx={{
            backgroundColor: "#fafaf6",
            border: "1px solid rgba(1,67,57,0.1)",
            borderRadius: 1,
            p: 1.5,
            pr: 6,
            m: 0,
            fontSize: 14,
            lineHeight: 1.5,
            fontFamily: paragraphFontFamily,
            color: "#0a0a0a",
            "& p": { m: 0, p: 0 },
            // Force a line-box on empty <p></p> spacers across browsers.
            "& p:empty": { minHeight: "1em" },
            "& p:empty::after": { content: '"\\200B"' },
            // ProseMirror shows a trailing <br> as a visible line; static HTML
            // collapses it, so re-add the line to match the tiptap editor.
            "& p:has(br:last-child)::after": { content: '"\\200B"' },
            "& a": {
              color: "#014339",
              textDecoration: "underline",
              cursor: "default",
              padding: 0,
              margin: 0,
            },
            "& .merge-tag, & .variable": {
              background: "#fce6df",
              color: "#0a0a0a",
              borderRadius: "0.4rem",
              padding: "0.1rem 0.1rem",
              boxDecorationBreak: "clone",
              WebkitBoxDecorationBreak: "clone",
            },
          }}
          dangerouslySetInnerHTML={{ __html: safeHtml }}
        />
      ) : (
        <Box
          component="pre"
          sx={{
            backgroundColor: "#fafaf6",
            border: "1px solid rgba(1,67,57,0.1)",
            borderRadius: 1,
            p: 1.5,
            pr: 6,
            m: 0,
            fontSize: 12,
            fontFamily:
              "ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace",
            whiteSpace: "pre-wrap",
            wordBreak: "break-word",
            overflowX: "auto",
          }}
        >
          {children}
        </Box>
      )}
    </Box>
  );
};

const TypingDots = () => (
  <Box sx={styles.typingDots} aria-label="Assistant is typing">
    <span />
    <span />
    <span />
  </Box>
);

const LoadingSkeleton = () => (
  <Box sx={styles.loadingStack}>
    <Skeleton
      animation="wave"
      variant="rounded"
      height={36}
      sx={styles.loadingBubbleUser}
    />
    <Skeleton
      animation="wave"
      variant="rounded"
      height={120}
      sx={styles.loadingBubbleAssistant}
    />
    <Skeleton
      animation="wave"
      variant="rounded"
      height={48}
      sx={styles.loadingBubbleUser}
    />
    <Skeleton
      animation="wave"
      variant="rounded"
      height={88}
      sx={styles.loadingBubbleAssistant}
    />
  </Box>
);

const AssistantPreview = () => {
  const { getToken } = useAuth();
  const { currentOrg } = useContext(OrganizationUserContext);
  const { setDiscoverTab, assistantOpenContext, setAssistantOpenContext } =
    useContext(DiscoverViewContext);
  const { subscription, setUpgradeDialogSource } =
    useContext(SubscriptionContext);
  const { setCurrentTemplateBody } = useContext(OutreachTemplatesContext);
  const { topLevelCategories, fetchBentoCategories } = useContext(
    BentoCategoriesContext,
  );
  const { getCampaigns } = useContext(CampaignsContext);
  const { decreaseChatCount } = useSubscription();
  const location = useLocation();
  const navigate = useNavigate();
  const urlPage = useMemo(
    () => derivePromptPage(location.pathname),
    [location.pathname],
  );
  const nicheOptions = useMemo(
    () => buildNicheOptions(topLevelCategories),
    [topLevelCategories],
  );

  const [conversationId, setConversationId] = useState<number | null>(null);
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [input, setInput] = useState("");
  const [streaming, setStreaming] = useState(false);
  const [bootstrapping, setBootstrapping] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [activeToolName, setActiveToolName] = useState<string | null>(null);
  const [messagesPage, setMessagesPage] = useState(1);
  const [hasMoreMessages, setHasMoreMessages] = useState(false);
  const [loadingOlder, setLoadingOlder] = useState(false);
  // The page + entity for the "Try asking" prompts. Initialized from wherever
  // the user is at open time, then updated only on a button press (a CTA / the
  // campaigns "AI" button sets `assistantOpenContext`). Plain navigation while
  // the chat stays open never changes them; closing and reopening re-initializes
  // from the current page.
  const [openPage, setOpenPage] = useState<PromptPage>(
    () =>
      assistantOpenContext?.promptPage ?? derivePromptPage(location.pathname),
  );
  const [openEntity, setOpenEntity] = useState<AssistantEntityContext | null>(
    () => assistantOpenContext?.entity ?? null,
  );
  const [dynamicFollowups, setDynamicFollowups] = useState<
    SuggestedPrompt[] | null
  >(null);
  const [dynamicFollowupsLoading, setDynamicFollowupsLoading] = useState(false);
  // `replyComplete` flips on `message_completed` so typing dots hide while sidecar still runs.
  const [replyComplete, setReplyComplete] = useState(false);
  const streamControllerRef = useRef<AbortController | null>(null);
  // One intro per button press: openAssistant hands us a fresh context object
  // each press, so we fire once per object even if dependent effects re-run.
  const lastIntroContextRef = useRef<AssistantOpenContext | null>(null);
  const lastAutoSendContextRef = useRef<AssistantOpenContext | null>(null);
  const isAutoIntro =
    assistantOpenContext?.source === ASSISTANT_AUTO_INTRO_SOURCE;
  const [introPhase, setIntroPhase] = useState<IntroPhase>(() =>
    isAutoIntro ? "typing" : "idle",
  );
  const [introWordCount, setIntroWordCount] = useState(0);
  const introStartedRef = useRef(false);
  const introIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const scrollRef = useRef<HTMLDivElement | null>(null);
  const isAtBottomRef = useRef(true);
  const [topSentinelRef, isTopSentinelVisible] = useInView({
    root: scrollRef.current,
    threshold: 0,
  });

  const isLoadingCredits = subscription === null;
  const remainingCredits = subscription?.remainingChatMessages ?? 0;
  const isPaidUser = Boolean(currentOrg?.isPaidUser);
  const outOfCredits = !isLoadingCredits && remainingCredits <= 0;

  const sidecarActive = streaming && replyComplete;
  // Live followups from a turn in this open session. While the chat stays open
  // they persist (and take precedence over page prompts), including across
  // navigation. A fresh open remounts the panel, resetting them to null.
  const followupsPresent =
    (sidecarActive || !streaming) &&
    (dynamicFollowupsLoading ||
      (dynamicFollowups !== null && dynamicFollowups.length > 0));
  // An unanswered picker IS the call to action — suppress competing prompts.
  const lastMessage = messages[messages.length - 1];
  const lastAssistantHasUnansweredPicker = Boolean(
    lastMessage &&
      lastMessage.role === "assistant" &&
      lastMessage.interactivePrompt &&
      !lastMessage.interactivePrompt.answered,
  );
  // With no live followups, fall back to the frozen page's prompts.
  const showPagePrompts =
    !bootstrapping &&
    !streaming &&
    !lastAssistantHasUnansweredPicker &&
    conversationId !== null &&
    !followupsPresent &&
    (introPhase === "idle" || introPhase === "shown");
  const shouldShowSuggestedPrompts =
    !lastAssistantHasUnansweredPicker && (followupsPresent || showPagePrompts);

  const { prompts: pagePrompts, loading: pagePromptsLoading } =
    useSuggestedPrompts(showPagePrompts ? openPage : null, 0, openEntity);

  const suggestedPrompts = followupsPresent
    ? (dynamicFollowups ?? []).slice(0, MAX_FOLLOWUP_PROMPTS)
    : pagePrompts.slice(0, MAX_PAGE_PROMPTS);
  const suggestedPromptsLoading = followupsPresent
    ? dynamicFollowupsLoading
    : pagePromptsLoading;

  useEffect(() => {
    if (!currentOrg?.id || conversationId !== null) return;
    let cancelled = false;
    (async () => {
      setBootstrapping(true);
      setError(null);
      try {
        const { conversation: conv } = await fetcherAuth(
          getToken,
          `/api/organization/${currentOrg.id}/assistant/conversations`,
          "POST",
        );
        if (cancelled || !conv?.id) return;
        const { messages: serverMessages } = await fetcherAuth(
          getToken,
          `/api/organization/${currentOrg.id}/assistant/conversations/${conv.id}?page=1&per_page=${MESSAGES_PER_PAGE}`,
          "GET",
        );
        if (cancelled) return;
        const rawMessages = serverMessages || [];
        const mapped = serverMessagesToChatMessages(rawMessages);
        setConversationId(conv.id);
        setMessages(mapped);
        setMessagesPage(1);
        setHasMoreMessages(rawMessages.length >= MESSAGES_PER_PAGE);
        // Don't restore the prior conversation's followup chips on open — a freshly
        // opened panel surfaces the current page's prompts. Followups only show
        // while the chat stays continuously open across a live turn.
      } catch (e) {
        if (!cancelled) setError((e as Error).message || "Failed to load");
      } finally {
        if (!cancelled) setBootstrapping(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [currentOrg?.id, conversationId, getToken]);

  // Persist + show one intro message per button press. Gated on a `source` so a
  // plain open (sidebar icon, no context) never creates one. Deduped by context
  // object so it fires once even though this re-runs after bootstrap.
  useEffect(() => {
    if (!currentOrg?.id || conversationId === null) return;
    if (bootstrapping) return;
    if (!assistantOpenContext?.source) return;
    // Auto-intro is UI-only — must not create a persisted intro message.
    if (assistantOpenContext.source === ASSISTANT_AUTO_INTRO_SOURCE) return;
    if (assistantOpenContext.initialMessage) return;
    if (lastIntroContextRef.current === assistantOpenContext) return;
    lastIntroContextRef.current = assistantOpenContext;

    let cancelled = false;
    (async () => {
      try {
        const { message } = await fetcherAuth(
          getToken,
          `/api/organization/${currentOrg.id}/assistant/conversations/${conversationId}/intro`,
          "POST",
          {},
          {
            page: assistantOpenContext.promptPage ?? urlPage,
            source: assistantOpenContext.source,
            entity_type: assistantOpenContext.entity?.entityType,
            entity_id: assistantOpenContext.entity?.entityId,
          },
        );
        if (cancelled || !message) return;
        const converted = serverMessagesToChatMessages([message]);
        if (converted.length === 0) return;
        setMessages((prev) => {
          const newId = converted[0].id;
          if (prev.some((m) => m.id === newId)) return prev;
          return [...prev, ...converted];
        });
      } catch {
        // Intro is non-critical — fail silently.
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [
    currentOrg?.id,
    conversationId,
    bootstrapping,
    assistantOpenContext,
    urlPage,
    getToken,
  ]);

  useEffect(() => {
    if (!currentOrg?.id || conversationId === null) return;
    if (bootstrapping) return;
    const initialMessage = assistantOpenContext?.initialMessage?.trim();
    if (!initialMessage) return;
    if (lastAutoSendContextRef.current === assistantOpenContext) return;
    lastAutoSendContextRef.current = assistantOpenContext;
    sendMessageWithText(initialMessage, "Floating Bubble");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentOrg?.id, conversationId, bootstrapping, assistantOpenContext]);

  useEffect(() => {
    if (!isAutoIntro || bootstrapping || conversationId === null) return;
    if (introStartedRef.current) return;
    introStartedRef.current = true;
    if (messages.length > 0) {
      setIntroPhase("shown");
      return;
    }
    const wordCount = AUTO_INTRO_TEXT.split(" ").length;
    setIntroPhase("typing");
    const startTimer = setTimeout(() => {
      setIntroPhase("streaming");
      let revealed = 0;
      introIntervalRef.current = setInterval(() => {
        revealed += 1;
        setIntroWordCount(revealed);
        if (revealed >= wordCount) {
          if (introIntervalRef.current) clearInterval(introIntervalRef.current);
          setIntroPhase("shown");
        }
      }, INTRO_WORD_MS);
    }, INTRO_TYPING_MS);
    return () => {
      clearTimeout(startTimer);
      if (introIntervalRef.current) clearInterval(introIntervalRef.current);
    };
  }, [isAutoIntro, bootstrapping, conversationId, messages.length]);

  // Sync the "Try asking" page/entity on a button press. openAssistant sets a
  // fresh context (always with a `source`); a plain open leaves it null, so the
  // prompts stay as initialized. This is the only thing that updates them while
  // the chat is open.
  useEffect(() => {
    if (!assistantOpenContext) return;
    setOpenPage(
      assistantOpenContext.promptPage ?? derivePromptPage(location.pathname),
    );
    setOpenEntity(assistantOpenContext.entity ?? null);
    // Drop any prior turn's follow-up chips so this press's page/entity prompts
    // surface — otherwise followupsPresent keeps them pinned over the new ones.
    setDynamicFollowups(null);
    setDynamicFollowupsLoading(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [assistantOpenContext]);

  // Auto-scroll on newest-message change; older-history prepends keep scroll position.
  const newestMessageId = messages[messages.length - 1]?.id;
  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [newestMessageId, streaming]);

  // Keep chips in view when they appear or when skeletons swap to real chips.
  const hasSuggestedPrompts =
    shouldShowSuggestedPrompts &&
    (suggestedPromptsLoading || suggestedPrompts.length > 0);
  useEffect(() => {
    if (!hasSuggestedPrompts) return;
    if (!isAtBottomRef.current) return;
    requestAnimationFrame(() => {
      if (scrollRef.current) {
        scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
      }
    });
  }, [hasSuggestedPrompts, suggestedPromptsLoading, suggestedPrompts.length]);

  // Gate picker scroll on `replyComplete` so the chip height is in the DOM; double-raf for commit.
  const lastMsg = messages[messages.length - 1];
  const lastPickerVisibleKey =
    lastMsg?.interactivePrompt && (!streaming || replyComplete)
      ? `${lastMsg.id}:${lastMsg.interactivePrompt.promptId}`
      : null;
  useEffect(() => {
    if (!lastPickerVisibleKey) return;
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        if (scrollRef.current) {
          scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
        }
      });
    });
  }, [lastPickerVisibleKey]);

  // On a button-press open, snap back to the bottom — covers the case where the
  // panel was already open and scrolled up to older history.
  useEffect(() => {
    if (!assistantOpenContext) return;
    requestAnimationFrame(() => {
      if (scrollRef.current) {
        scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
      }
    });
  }, [assistantOpenContext]);

  useEffect(() => {
    if (
      !isTopSentinelVisible ||
      !hasMoreMessages ||
      loadingOlder ||
      bootstrapping ||
      conversationId === null ||
      !currentOrg?.id
    ) {
      return;
    }
    const container = scrollRef.current;
    const distanceFromBottom = container
      ? container.scrollHeight - container.scrollTop
      : 0;
    setLoadingOlder(true);
    let cancelled = false;
    (async () => {
      const nextPage = messagesPage + 1;
      try {
        const { messages: olderRaw } = await fetcherAuth(
          getToken,
          `/api/organization/${currentOrg.id}/assistant/conversations/${conversationId}?page=${nextPage}&per_page=${MESSAGES_PER_PAGE}`,
          "GET",
        );
        if (cancelled) return;
        const olderRawArr = olderRaw || [];
        const older = serverMessagesToChatMessages(olderRawArr);
        setMessagesPage(nextPage);
        if (olderRawArr.length < MESSAGES_PER_PAGE) setHasMoreMessages(false);
        if (older.length > 0) {
          setMessages((prev) => [...older, ...prev]);
          requestAnimationFrame(() => {
            if (container) {
              container.scrollTop = container.scrollHeight - distanceFromBottom;
            }
          });
        }
      } catch (e) {
        if (!cancelled) {
          setError((e as Error).message || "Failed to load earlier messages");
        }
      } finally {
        setLoadingOlder(false);
      }
    })();
    return () => {
      cancelled = true;
    };
    // `loadingOlder`/`messagesPage` are deliberately off the dep array — listing them
    // would let this effect's own state writes trip the cleanup.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    isTopSentinelVisible,
    hasMoreMessages,
    bootstrapping,
    conversationId,
    currentOrg?.id,
  ]);

  // The niche picker fills its options from BentoCategoriesContext; load the
  // tree on demand if no other page has fetched it yet. One-shot via a ref —
  // the context doesn't auto-fetch and its `categoriesLoading` starts true, so
  // we can't gate on that.
  const needsNicheOptions = messages.some(
    (m) =>
      m.interactivePrompt &&
      !m.interactivePrompt.answered &&
      m.interactivePrompt.promptId === CATEGORIES_PROMPT_ID,
  );
  const nicheFetchStartedRef = useRef(false);
  useEffect(() => {
    if (
      needsNicheOptions &&
      topLevelCategories.length === 0 &&
      !nicheFetchStartedRef.current
    ) {
      nicheFetchStartedRef.current = true;
      fetchBentoCategories();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [needsNicheOptions, topLevelCategories.length]);

  const closeDialog = () => setDiscoverTab(null);

  const handleBuyMore = () => {
    setUpgradeDialogSource(
      UpgradeDialogSource.hits_personalized_ai_messages_limit,
    );
  };

  const handleChoiceConfirm = (
    messageId: string,
    selected: InteractiveChoiceOption[],
  ) => {
    if (selected.length === 0) return;
    setMessages((prev) =>
      prev.map((m) =>
        m.id === messageId && m.interactivePrompt
          ? {
              ...m,
              interactivePrompt: {
                ...m.interactivePrompt,
                answered: true,
                selected,
              },
            }
          : m,
      ),
    );
    // Send labels only; the model resolves IDs from prior `list_bento_categories` results.
    const formatted = selected.map((o) => o.label).join(", ");
    sendMessageWithText(formatted, "Choice Picker");
  };

  const sendMessage = () => sendMessageWithText(input.trim(), "Typed");

  const sendMessageWithText = async (
    text: string,
    source: MessageSource = "Typed",
    metadata?: ChipMetadata,
  ) => {
    if (!text || !currentOrg?.id || conversationId === null) return;
    if (outOfCredits) {
      handleBuyMore();
      return;
    }
    // Cancel any in-flight prior stream so two don't overlap.
    streamControllerRef.current?.abort();
    const controller = new AbortController();
    streamControllerRef.current = controller;
    setInput("");
    setError(null);
    setActiveToolName(null);
    setAssistantOpenContext(null);
    setDynamicFollowups(null);
    setDynamicFollowupsLoading(false);
    setReplyComplete(false);
    const now = Date.now();
    setMessages((prev) => [
      ...prev,
      { id: `user-${now}`, role: "user", text, createdAt: now },
    ]);
    setStreaming(true);

    let assistantText = "";
    let creditDecremented = false;
    const assistantId = `assistant-${now}`;
    setMessages((prev) => [
      ...prev,
      { id: assistantId, role: "assistant", text: "", createdAt: Date.now() },
    ]);

    try {
      const res = await fetcherAuth(
        getToken,
        `/api/organization/${currentOrg.id}/assistant/conversations/${conversationId}/messages`,
        "POST",
        {},
        {
          content: text,
          page: urlPage,
          source,
          ...(metadata ? { metadata } : {}),
        },
        true,
        false,
        true,
        controller.signal,
      );
      if (!res || !res.ok || !res.body) {
        const body = await res.text();
        throw new Error(`HTTP ${res.status}: ${body || "no body"}`);
      }
      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const frames = buffer.split("\n\n");
        buffer = frames.pop() || "";
        for (const frame of frames) {
          if (!frame.trim()) continue;
          const lines = frame.split("\n");
          let event = "message";
          let data = "";
          for (const line of lines) {
            if (line.startsWith("event: ")) event = line.slice(7);
            else if (line.startsWith("data: ")) data += line.slice(6);
          }
          let parsed: Record<string, unknown> | null = null;
          try {
            parsed = data
              ? (JSON.parse(data) as Record<string, unknown>)
              : null;
          } catch {
            continue;
          }
          if (event === "message_delta" || event === "message_completed") {
            const chunk = typeof parsed?.text === "string" ? parsed.text : "";
            if (!chunk) continue;
            setActiveToolName(null);
            if (event === "message_completed") {
              assistantText = chunk;
            } else {
              assistantText += chunk;
            }
            const finalText = assistantText;
            setMessages((prev) =>
              prev.map((m) =>
                m.id === assistantId ? { ...m, text: finalText } : m,
              ),
            );
            if (event === "message_completed") {
              setReplyComplete(true);
              if (!creditDecremented) {
                creditDecremented = true;
                decreaseChatCount();
              }
            }
          } else if (event === "tool_call_started") {
            setActiveToolName(
              typeof parsed?.tool_name === "string" ? parsed.tool_name : null,
            );
          } else if (event === "tool_call_finished") {
            setActiveToolName(null);
            // A drafted campaign won't show on the Campaigns page until we
            // refetch — the wizard creates it server-side, outside this tab.
            if (parsed?.tool_name === "create_campaign_draft_from_chat") {
              getCampaigns(1);
            }
          } else if (event === "interactive_prompt") {
            setActiveToolName(null);
            const rawOptions = parsed?.options;
            const promptPayload: InteractivePrompt = {
              promptId:
                typeof parsed?.prompt_id === "string" ? parsed.prompt_id : "",
              question:
                typeof parsed?.question === "string" ? parsed.question : "",
              options: Array.isArray(rawOptions)
                ? (rawOptions as InteractiveChoiceOption[])
                : [],
              multiSelect: Boolean(parsed?.multi_select),
              minSelections:
                typeof parsed?.min_selections === "number"
                  ? parsed.min_selections
                  : 1,
              maxSelections:
                typeof parsed?.max_selections === "number"
                  ? parsed.max_selections
                  : null,
              answered: false,
              selected: [],
            };
            setMessages((prev) =>
              prev.map((m) =>
                m.id === assistantId
                  ? { ...m, interactivePrompt: promptPayload }
                  : m,
              ),
            );
          } else if (event === "suggested_followups_loading") {
            setDynamicFollowupsLoading(true);
          } else if (event === "suggested_followups") {
            setDynamicFollowupsLoading(false);
            const rawChips = parsed?.chips;
            const chips = Array.isArray(rawChips)
              ? (rawChips as SuggestedPrompt[])
              : [];
            setDynamicFollowups(chips.length > 0 ? chips : null);
          } else if (event === "error") {
            setError(
              typeof parsed?.message === "string"
                ? parsed.message
                : "Stream error",
            );
          }
        }
      }
    } catch (e) {
      if (controller.signal.aborted) return;
      setError((e as Error).message || "Send failed");
    } finally {
      // Only stop the spinner if this is still the active stream.
      if (streamControllerRef.current === controller) {
        streamControllerRef.current = null;
        setStreaming(false);
        setActiveToolName(null);
        // Fallback when the stream closes with no text or chip prompt.
        setMessages((prev) =>
          prev.map((m) =>
            m.id === assistantId && !m.text && !m.interactivePrompt
              ? {
                  ...m,
                  text: "Hmm, I didn't get a reply that time. Try again?",
                }
              : m,
          ),
        );
      }
    }
  };

  return (
    <Box sx={styles.root}>
      <Box sx={styles.header}>
        <Grid container justifyContent="space-between" alignItems="center">
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Box
              component="i"
              className="fa-regular fa-comments"
              sx={styles.headerIcon}
            />
            <Typography sx={styles.headerTitle}>Assistant</Typography>
          </Box>
          <IconButton sx={styles.close} onClick={closeDialog}>
            <Box component="i" className="fa-regular fa-xmark" />
          </IconButton>
        </Grid>
        <Box sx={styles.creditsBanner}>
          <Typography sx={styles.creditsText}>
            {isLoadingCredits
              ? "Loading credits…"
              : `${remainingCredits} credit${
                  remainingCredits === 1 ? "" : "s"
                } left`}
            <Tooltip
              slotProps={{
                tooltip: {
                  sx: {
                    color: "black",
                    backgroundColor: "white",
                    p: 1,
                    boxShadow: 1,
                  },
                },
              }}
              title={AI_DRAFT_CREDITS_TOOLTIP}
              enterTouchDelay={0}
            >
              <Box
                component="i"
                className="fa-solid fa-circle-info"
                sx={styles.creditsInfoIcon}
              />
            </Tooltip>
          </Typography>
          {!isPaidUser && (
            <Button
              size="small"
              disableElevation
              onClick={handleBuyMore}
              sx={styles.buyMoreButton}
            >
              Buy more
            </Button>
          )}
        </Box>
      </Box>
      <Box
        ref={scrollRef}
        sx={styles.messages}
        onScroll={() => {
          const el = scrollRef.current;
          if (!el) return;
          isAtBottomRef.current =
            el.scrollHeight - el.scrollTop - el.clientHeight < 50;
        }}
      >
        {bootstrapping && <LoadingSkeleton />}
        {hasMoreMessages && !bootstrapping && (
          <Box ref={topSentinelRef} sx={{ height: 1 }} />
        )}
        {loadingOlder && <LoadingSkeleton />}
        {messages.length === 0 && !bootstrapping && introPhase === "idle" && (
          <Typography variant="body2" color="text.secondary">
            Ask me anything about your outreach.
          </Typography>
        )}
        {isAutoIntro &&
          !bootstrapping &&
          messages.length === 0 &&
          introPhase !== "idle" && (
            <Box sx={styles.assistantBubble}>
              <Box sx={styles.assistantText}>
                {introPhase === "typing" ? (
                  <TypingDots />
                ) : (
                  <Box sx={{ "& p": { m: 0, my: 0.25 } }}>
                    <ReactMarkdown remarkPlugins={[remarkGfm]}>
                      {introPhase === "shown"
                        ? AUTO_INTRO_TEXT
                        : AUTO_INTRO_TEXT.split(" ")
                            .slice(0, introWordCount)
                            .join(" ")}
                    </ReactMarkdown>
                    {introPhase === "streaming" && (
                      <Box component="span" sx={styles.inlineTyping}>
                        <TypingDots />
                      </Box>
                    )}
                  </Box>
                )}
              </Box>
            </Box>
          )}
        {messages.map((m, idx) => {
          const isAssistant = m.role === "assistant";
          const isLast = idx === messages.length - 1;
          const isActivelyStreaming =
            streaming && isAssistant && isLast && !replyComplete;
          const templatePrefillByHref = isAssistant
            ? buildTemplatePrefillMap(normalizeAssistantText(m.text || ""))
            : new Map<string, string>();
          return (
            <Fragment key={m.id}>
              <Box
                sx={
                  m.role === "user" ? styles.userBubble : styles.assistantBubble
                }
              >
                {isAssistant ? (
                  <Box sx={styles.assistantText}>
                    {m.text || m.interactivePrompt ? (
                      <>
                        <Box
                          sx={{
                            "& p": { m: 0, my: 0.25 },
                            "& ul, & ol": {
                              m: 0,
                              whiteSpace: "normal",
                              paddingLeft: "1.25em",
                            },
                            "& li": { marginBottom: 0.5 },
                            "& a": styles.assistantLink,
                          }}
                        >
                          <ReactMarkdown
                            remarkPlugins={[remarkGfm]}
                            components={{
                              pre: ({ children }) => (
                                <CodeBlock
                                  language={extractCodeLanguage(children)}
                                >
                                  {children}
                                </CodeBlock>
                              ),
                              a: ({ node, children, href, ...props }) => {
                                const internalPath =
                                  typeof href === "string"
                                    ? getInternalPath(href)
                                    : null;
                                if (internalPath) {
                                  return (
                                    <a
                                      {...props}
                                      href={internalPath}
                                      onClick={(e) => {
                                        if (
                                          e.metaKey ||
                                          e.ctrlKey ||
                                          e.shiftKey ||
                                          e.altKey
                                        ) {
                                          return;
                                        }
                                        e.preventDefault();
                                        const prefill =
                                          templatePrefillByHref.get(
                                            internalPath,
                                          ) ??
                                          templatePrefillByHref.get(
                                            href as string,
                                          );
                                        if (prefill) {
                                          setCurrentTemplateBody(prefill);
                                        }
                                        trackEvent(
                                          "Assistant Deep Link Clicked",
                                          {
                                            Link: internalPath,
                                            "Link Type": "Internal",
                                          },
                                        );
                                        navigate(internalPath);
                                      }}
                                    >
                                      {children}
                                    </a>
                                  );
                                }
                                return (
                                  <a
                                    {...props}
                                    href={href}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    onClick={() => {
                                      trackEvent(
                                        "Assistant Deep Link Clicked",
                                        {
                                          Link: href,
                                          "Link Type": "External",
                                        },
                                      );
                                    }}
                                  >
                                    {children}
                                  </a>
                                );
                              },
                            }}
                          >
                            {normalizeAssistantText(m.text)}
                          </ReactMarkdown>
                        </Box>
                        {isActivelyStreaming && (
                          <Box component="span" sx={styles.inlineTyping}>
                            <TypingDots />
                          </Box>
                        )}
                      </>
                    ) : isActivelyStreaming ? (
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: 1,
                        }}
                      >
                        <TypingDots />
                        {activeToolName && (
                          <Typography
                            variant="body2"
                            sx={{ color: "#4A5565", fontStyle: "italic" }}
                          >
                            {toolStatusLabel(activeToolName)}…
                          </Typography>
                        )}
                      </Box>
                    ) : null}
                  </Box>
                ) : (
                  <Box
                    sx={{
                      "& p": { m: 0, my: 0.25 },
                      "& a": styles.userLink,
                    }}
                  >
                    <ReactMarkdown
                      remarkPlugins={[remarkGfm]}
                      components={{
                        a: ({ node, children, href, ...props }) => {
                          const internalPath =
                            typeof href === "string"
                              ? getInternalPath(href)
                              : null;
                          if (internalPath) {
                            return (
                              <a
                                {...props}
                                href={internalPath}
                                onClick={(e) => {
                                  if (
                                    e.metaKey ||
                                    e.ctrlKey ||
                                    e.shiftKey ||
                                    e.altKey
                                  ) {
                                    return;
                                  }
                                  e.preventDefault();
                                  navigate(internalPath);
                                }}
                              >
                                {children}
                              </a>
                            );
                          }
                          return (
                            <a
                              {...props}
                              href={href}
                              target="_blank"
                              rel="noopener noreferrer"
                            >
                              {children}
                            </a>
                          );
                        },
                      }}
                    >
                      {m.text}
                    </ReactMarkdown>
                  </Box>
                )}
              </Box>
              {m.interactivePrompt && !isActivelyStreaming && (
                <Box sx={{ pb: isLast ? 10 : 0 }}>
                  <InteractiveChoicePicker
                    question={m.interactivePrompt.question}
                    options={
                      m.interactivePrompt.promptId === CATEGORIES_PROMPT_ID
                        ? nicheOptions
                        : m.interactivePrompt.options
                    }
                    multiSelect={m.interactivePrompt.multiSelect}
                    minSelections={m.interactivePrompt.minSelections}
                    maxSelections={m.interactivePrompt.maxSelections}
                    disabled={m.interactivePrompt.answered}
                    hideQuestion={Boolean(m.text)}
                    initialSelected={m.interactivePrompt.selected}
                    onConfirm={(picked) => handleChoiceConfirm(m.id, picked)}
                  />
                </Box>
              )}
            </Fragment>
          );
        })}
        {error && (
          <Paper variant="outlined" sx={{ p: 1.5, borderColor: "error.main" }}>
            <Typography variant="caption" color="error">
              {error}
            </Typography>
          </Paper>
        )}
        {shouldShowSuggestedPrompts && (
          <Box sx={{ pb: 10 }}>
            <SuggestedPrompts
              prompts={suggestedPrompts}
              loading={suggestedPromptsLoading}
              onPick={(prompt, metadata) => {
                trackEvent("Suggested Prompt Clicked", {
                  Page: openPage,
                  Prompt: prompt,
                  "Is Followup": followupsPresent,
                  "Auto Intro": isAutoIntro,
                  ...(metadata?.entity_type
                    ? { "Entity Type": metadata.entity_type }
                    : {}),
                });
                sendMessageWithText(prompt, "Suggested Prompt", metadata);
              }}
            />
          </Box>
        )}
      </Box>
      <Box sx={styles.input}>
        <TextField
          fullWidth
          size="small"
          placeholder="Ask the assistant…"
          value={input}
          disabled={
            (streaming && !replyComplete) ||
            bootstrapping ||
            conversationId === null
          }
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter" && !e.shiftKey) {
              e.preventDefault();
              sendMessage();
            }
          }}
        />
        <IconButton
          onClick={sendMessage}
          disabled={
            !input.trim() ||
            (streaming && !replyComplete) ||
            bootstrapping ||
            conversationId === null
          }
          sx={styles.sendButton}
        >
          <Box
            component="i"
            className="fa-solid fa-paper-plane-top"
            sx={styles.sendIcon}
          />
        </IconButton>
      </Box>
    </Box>
  );
};

// Pulls a `prompt_user_choice` function_call out of an ADK Event item, when
// present. Returns null if this message isn't an active picker.
function extractInteractivePrompt(
  item: Record<string, unknown> | undefined,
): InteractivePrompt | null {
  const content = (item?.content ?? null) as {
    parts?: Array<Record<string, unknown>>;
  } | null;
  const parts = content?.parts ?? [];
  for (const part of parts) {
    const call = part.function_call as
      | { name?: string; args?: Record<string, unknown> }
      | undefined;
    if (!call || call.name !== "prompt_user_choice") continue;
    const args = call.args ?? {};
    const rawOptions = Array.isArray(args.options) ? args.options : [];
    const options = rawOptions
      .map(
        (o) => o as { value?: string | number; label?: string; group?: string },
      )
      .filter((o) => o && o.label !== undefined && o.value !== undefined)
      .map((o) => ({
        value: o.value as string | number,
        label: String(o.label),
        ...(o.group !== undefined ? { group: String(o.group) } : {}),
      }));
    return {
      promptId: String(args.prompt_id ?? ""),
      question: String(args.question ?? ""),
      options,
      multiSelect: Boolean(args.multi_select),
      minSelections:
        typeof args.min_selections === "number" ? args.min_selections : 1,
      maxSelections:
        typeof args.max_selections === "number" ? args.max_selections : null,
      answered: false,
      selected: [],
    };
  }
  return null;
}

// Collapse ADK Event rows into FE bubbles; attaches pickers to their text bubble
// (or its own when text-less) and marks all but the last picker as answered/stale.
function serverMessagesToChatMessages(
  messages: ServerMessage[],
): ChatMessage[] {
  const out: ChatMessage[] = [];
  let pendingPicker: InteractivePrompt | null = null;
  let pendingPickerSourceId: number | null = null;

  const flushPendingPickerAsBubble = () => {
    if (!pendingPicker || pendingPickerSourceId === null) return;
    out.push({
      id: `server-${pendingPickerSourceId}`,
      role: "assistant",
      text: "",
      createdAt: Date.now(),
      interactivePrompt: pendingPicker,
    });
    pendingPicker = null;
    pendingPickerSourceId = null;
  };

  const markLastPickerAnswered = (userText: string) => {
    if (out.length === 0) return;
    const last = out[out.length - 1];
    if (!last.interactivePrompt || last.interactivePrompt.answered) return;
    const labels = userText
      .split(",")
      .map((s) => s.trim().toLowerCase())
      .filter(Boolean);
    const selected = last.interactivePrompt.options.filter((o) =>
      labels.includes(o.label.toLowerCase()),
    );
    last.interactivePrompt = {
      ...last.interactivePrompt,
      answered: true,
      selected,
    };
  };

  for (const m of messages) {
    const createdAt = m.created_at ? Date.parse(m.created_at) : Date.now();
    const safeCreated = Number.isNaN(createdAt) ? Date.now() : createdAt;
    const item = m.item || {};

    if (m.role === "user") {
      // Flush pending picker first so we have something to mark as answered.
      flushPendingPickerAsBubble();
      markLastPickerAnswered(m.text || "");
      if (!m.text) continue;
      out.push({
        id: `server-${m.id}`,
        role: "user",
        text: m.text,
        createdAt: safeCreated,
      });
      continue;
    }

    const picker = extractInteractivePrompt(item as Record<string, unknown>);

    if (!m.text) {
      // Text-less row — hold any picker for the next assistant bubble; skip otherwise.
      if (picker) {
        pendingPicker = picker;
        pendingPickerSourceId = m.id;
      }
      continue;
    }

    const role =
      m.role === "assistant" || m.role === "tool"
        ? (m.role as "assistant" | "tool")
        : "system";
    const chat: ChatMessage = {
      id: `server-${m.id}`,
      role,
      text: m.text,
      createdAt: safeCreated,
    };
    // Same-message picker takes precedence; flush any older pending one first.
    if (picker) {
      if (pendingPicker) flushPendingPickerAsBubble();
      chat.interactivePrompt = picker;
    } else if (pendingPicker) {
      chat.interactivePrompt = pendingPicker;
      pendingPicker = null;
      pendingPickerSourceId = null;
    }
    out.push(chat);
  }

  // Trailing picker with no text turn after it — render as its own bubble.
  flushPendingPickerAsBubble();

  // Only the last bubble's picker stays interactive; mark earlier ones answered/stale.
  for (let i = 0; i < out.length - 1; i++) {
    const prompt = out[i].interactivePrompt;
    if (prompt && !prompt.answered) {
      out[i].interactivePrompt = { ...prompt, answered: true };
    }
  }

  return out;
}

export default AssistantPreview;
export { AssistantPreview };
